import { Link } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { useCart } from '../../contexts/CartContext';
import { useState, useEffect } from 'react';
import { STORAGE_KEY, DURATION } from '../membership/components/CountdownTimer';

export default function CartPage() {
  const { items, removeFromCart, getCartTotal } = useCart();
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [timerActive, setTimerActive] = useState(false);

  useEffect(() => {
    const startTime = parseInt(localStorage.getItem(STORAGE_KEY) || '0', 10);
    if (!startTime || isNaN(startTime)) {
      setTimerActive(false);
      return;
    }

    const update = () => {
      const elapsed = Date.now() - startTime;
      const remaining = DURATION - elapsed;
      if (remaining <= 0) {
        setTimeLeft(0);
        setTimerActive(false);
      } else {
        setTimeLeft(remaining);
        setTimerActive(true);
      }
    };

    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, []);

  const minutes = Math.floor(timeLeft / 60000);
  const seconds = Math.floor((timeLeft % 60000) / 1000);

  const subtotal = getCartTotal(false);
  const discount = timerActive ? subtotal * 0.15 : 0;
  const total = subtotal - discount;

  return (
    <div className="min-h-screen flex flex-col bg-sand">
      <Navigation />
      
      <main className="flex-grow pt-24 pb-16 px-6 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-light text-charcoal mb-8 tracking-wide">
            Your Cart
          </h1>

          {items.length === 0 ? (
            <div className="bg-bone rounded-2xl p-12 text-center">
              <i className="ri-shopping-cart-line text-6xl text-greige mb-4"></i>
              <p className="text-xl font-light text-greige mb-6">Your cart is empty</p>
              <Link
                to="/membership"
                className="inline-block px-8 py-3 bg-charcoal text-white text-sm font-light tracking-wide rounded-full hover:bg-charcoal/90 transition-all whitespace-nowrap cursor-pointer"
              >
                Browse Memberships
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Cart Items */}
              <div className="lg:col-span-2 space-y-4">
                {items.map((item) => (
                  <div key={item.id} className="bg-bone rounded-2xl p-6 flex flex-col md:flex-row gap-6">
                    <div className="flex-grow">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="text-xl font-light text-charcoal tracking-wide mb-1">
                            {item.name}
                          </h3>
                          <p className="text-sm text-greige font-light">{item.ivCount}</p>
                        </div>
                        <button
                          onClick={() => removeFromCart(item.id)}
                          className="text-greige hover:text-charcoal transition-colors cursor-pointer"
                          aria-label="Remove item"
                        >
                          <i className="ri-close-line text-2xl"></i>
                        </button>
                      </div>
                      
                      <div className="mt-4">
                        <p className="text-2xl font-light text-gold">
                          {timerActive && item.discountedPrice ? item.discountedPrice : item.price}
                          <span className="text-sm text-greige ml-2">{item.period}</span>
                        </p>
                        {timerActive && item.discountedPrice && (
                          <p className="text-sm text-greige line-through mt-1">{item.price}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Order Summary */}
              <div className="lg:col-span-1">
                <div className="bg-bone rounded-2xl p-8 sticky top-24">
                  <h2 className="text-2xl font-light text-charcoal mb-6 tracking-wide">
                    Order Summary
                  </h2>

                  {timerActive && (
                    <div className="mb-6 p-4 bg-gold/10 border border-gold/30 rounded-xl">
                      <p className="text-sm font-light text-charcoal mb-2 text-center">
                        Book within <strong>10 minutes</strong> to get <strong className="text-gold">15% off</strong>
                      </p>
                      <div className="flex items-center justify-center gap-2">
                        <div className="flex flex-col items-center bg-white rounded-lg px-3 py-2 min-w-[48px]">
                          <span className="text-xl font-light text-charcoal tabular-nums leading-none">
                            {String(minutes).padStart(2, '0')}
                          </span>
                          <span className="text-[9px] tracking-widest text-greige uppercase">min</span>
                        </div>
                        <span className="text-lg font-light text-gold">:</span>
                        <div className="flex flex-col items-center bg-white rounded-lg px-3 py-2 min-w-[48px]">
                          <span className="text-xl font-light text-charcoal tabular-nums leading-none">
                            {String(seconds).padStart(2, '0')}
                          </span>
                          <span className="text-[9px] tracking-widest text-greige uppercase">sec</span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between text-greige">
                      <span className="font-light">Subtotal</span>
                      <span className="font-light">${subtotal.toFixed(2)}</span>
                    </div>
                    
                    {timerActive && discount > 0 && (
                      <div className="flex justify-between text-gold">
                        <span className="font-light">Discount (15%)</span>
                        <span className="font-light">-${discount.toFixed(2)}</span>
                      </div>
                    )}
                    
                    <div className="border-t border-greige/20 pt-3 flex justify-between text-charcoal">
                      <span className="text-lg font-light">Total</span>
                      <span className="text-2xl font-light">${total.toFixed(2)}</span>
                    </div>
                  </div>

                  <Link
                    to="/booking"
                    className="block w-full py-3 text-center bg-charcoal text-white text-sm font-light tracking-wide rounded-full hover:bg-charcoal/90 transition-all whitespace-nowrap cursor-pointer mb-3"
                  >
                    Continue to Booking
                  </Link>

                  <Link
                    to="/membership"
                    className="block w-full py-3 text-center bg-transparent border border-charcoal text-charcoal text-sm font-light tracking-wide rounded-full hover:bg-charcoal/5 transition-all whitespace-nowrap cursor-pointer"
                  >
                    Add More Items
                  </Link>

                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}